import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Check, X, QrCode, Search, Loader2, Copy, RefreshCw, FileText, MessageCircle } from 'lucide-react';
import { useConfirmDialog } from '@/components/useConfirmDialog';
import { QRCodeSVG } from 'qrcode.react';

const statusColors = {
  aguardando_pagamento: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
  pago: 'bg-green-400/10 text-green-400 border-green-400/20',
  atrasado: 'bg-red-400/10 text-red-400 border-red-400/20',
  cancelado: 'bg-zinc-400/10 text-zinc-400 border-zinc-400/20',
  comprovante_enviado: 'bg-blue-400/10 text-blue-400 border-blue-400/20',
};

const statusLabel = {
  aguardando_pagamento: 'Aguardando',
  pago: 'Pago',
  atrasado: 'Inadimplente',
  cancelado: 'Cancelado',
  comprovante_enviado: 'Comprovante enviado'
};

const categoriasFiltro = [
  { key: 'sub14', label: 'Sub 14' },
  { key: 'sub16', label: 'Sub 16' },
  { key: 'sub18', label: 'Sub 18' },
  { key: 'sub21', label: 'Sub 21' },
  { key: 'adulto', label: 'Adulto' },
];

function idadeNoAno(dataNascimento) {
  if (!dataNascimento) return null;

  const anoNascimento = Number(String(dataNascimento).slice(0, 4));

  if (!anoNascimento) return null;

  return new Date().getFullYear() - anoNascimento;
}

function categoriaDoAluno(aluno) {
  const idade = idadeNoAno(aluno?.data_nascimento);

  if (idade == null) return null;
  if (idade <= 13) return 'sub14';
  if (idade <= 15) return 'sub16';
  if (idade <= 17) return 'sub18';
  if (idade <= 20) return 'sub21';

  return 'adulto';
}

function categoriaCompletaDoAluno(aluno) {
  const categoria = categoriaDoAluno(aluno);

  if (!categoria) return null;

  const sexo =
    aluno?.sexo === 'masculino' || aluno?.sexo === 'feminino'
      ? aluno.sexo
      : 'nao_informado';

  return `${sexo}:${categoria}`;
}

function formatWhatsApp(phone) {
  if (!phone) return null;

  const digits = String(phone).replace(/\D/g, '');

  if (digits.length < 10) return null;

  return digits.length <= 11 ? `55${digits}` : digits;
}

function getContatoPreferencial(aluno) {
  if (!aluno) return null;

  const telefoneAluno = formatWhatsApp(aluno.phone);
  const telefoneResponsavel = formatWhatsApp(aluno.responsavel_whatsapp);

  if (aluno.contato_preferencial === 'responsavel') {
    return telefoneResponsavel || telefoneAluno;
  }

  return telefoneAluno || telefoneResponsavel;
}

function formatarDataBrasileira(data) {
  const dataLimpa = String(data || '').split('T')[0];
  const partes = dataLimpa.split('-');

  if (partes.length !== 3) return dataLimpa || 'Não informado';

  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function diferencaDiasParaVencimento(dataVencimento) {
  const dataLimpa = String(dataVencimento || '').split('T')[0];
  const partes = dataLimpa.split('-').map(Number);

  if (
    partes.length !== 3 ||
    !partes[0] ||
    !partes[1] ||
    !partes[2]
  ) {
    return null;
  }

  const hoje = new Date();

  const hojeUtc = Date.UTC(
    hoje.getFullYear(),
    hoje.getMonth(),
    hoje.getDate()
  );

  const vencimentoUtc = Date.UTC(
    partes[0],
    partes[1] - 1,
    partes[2]
  );

  return Math.round(
    (vencimentoUtc - hojeUtc) / (1000 * 60 * 60 * 24)
  );
}

function formatarValor(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}

function getAcaoWhatsAppMensalidade(mensalidade, aluno) {
  if (!mensalidade || !aluno) return null;

  if (
    mensalidade.status === 'pago' ||
    mensalidade.status === 'cancelado'
  ) {
    return null;
  }

  const telefone = getContatoPreferencial(aluno);

  if (!telefone) return null;

  const dias = diferencaDiasParaVencimento(
    mensalidade.data_vencimento
  );

  if (dias == null) return null;

  const nome = mensalidade.aluno_nome || aluno.name || 'aluno';
  const valor = formatarValor(mensalidade.valor);
  const vencimento = formatarDataBrasileira(
    mensalidade.data_vencimento
  );
  const competencia = mensalidade.competencia || 'Não informada';

  let tipo;
  let titulo;
  let mensagem;

  if (dias === 2) {
    tipo = 'lembrete';
    titulo = 'Enviar lembrete: faltam 2 dias';

    mensagem = `Olá, ${nome}! Tudo bem?

Passando para lembrar que sua mensalidade vence em 2 dias.

Competência: ${competencia}
Valor: ${valor}
Vencimento: ${vencimento}

Caso já tenha realizado o pagamento, desconsidere esta mensagem.

Qualquer dúvida, estamos à disposição.`;
  } else if (dias === 0) {
    tipo = 'vence_hoje';
    titulo = 'Enviar lembrete: vence hoje';

    mensagem = `Olá, ${nome}! Tudo bem?

Passando para lembrar que sua mensalidade vence hoje.

Competência: ${competencia}
Valor: ${valor}
Vencimento: ${vencimento}

Caso já tenha realizado o pagamento, desconsidere esta mensagem.

Qualquer dúvida, estamos à disposição.`;
  } else if (dias < 0) {
    tipo = 'cobranca';
    titulo = 'Enviar cobrança';

    mensagem = `Olá, ${nome}! Tudo bem?

Identificamos que sua mensalidade está em aberto.

Competência: ${competencia}
Valor: ${valor}
Vencimento: ${vencimento}

Pedimos, por gentileza, que regularize o pagamento para manter sua participação nos treinos normalmente.

Caso já tenha realizado o pagamento, desconsidere esta mensagem.

Qualquer dúvida, estamos à disposição.`;
  } else {
    // Opção A:
    // não mostra o botão antes do momento adequado.
    return null;
  }

  if (mensalidade.pix_code) {
    mensagem += `

Código PIX copia e cola:

${mensalidade.pix_code}`;
  }

  const link = `https://wa.me/${telefone}?text=${encodeURIComponent(
    mensagem
  )}`;

  return {
    tipo,
    titulo,
    link
  };
}

export default function MensalidadesPage() {
  const [mensalidades, setMensalidades] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filtroTurma, setFiltroTurma] = useState('todas');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [filtroPlano, setFiltroPlano] = useState('todos');
  const [filtroVencimento, setFiltroVencimento] = useState('todos');
  const [filtroCompetencia, setFiltroCompetencia] = useState('todas');
  const [ordenacao, setOrdenacao] = useState('vencimento-recente');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pixDialog, setPixDialog] = useState(null);
  const [comprovanteDialog, setComprovanteDialog] = useState(null);
  const [gerarDialog, setGerarDialog] = useState(false);
  const [form, setForm] = useState({ aluno_id: '', plano_id: '', valor: '', data_vencimento: '', competencia: '', observacoes: '' });
  const [gerarForm, setGerarForm] = useState({ competencia: '' });
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const fetchData = useCallback(async () => {
    try {
      const [m, a, t, p] = await Promise.all([
        api.get('/gestor/mensalidades'),
        api.get('/gestor/alunos'),
        api.get('/gestor/turmas'),
        api.get('/gestor/planos')
      ]);

      setMensalidades(m.data || []);
      setAlunos(a.data || []);
      setTurmas(t.data || []);
      setPlanos(p.data || []);
    } catch { toast.error('Erro ao carregar'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/gestor/mensalidades', { ...form, valor: parseFloat(form.valor) });
      toast.success('Mensalidade criada');
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const handleGerar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post(`/gestor/mensalidades/gerar?competencia=${gerarForm.competencia}`);
      toast.success(r.data.message);
      setGerarDialog(false);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const confirmar = async (id) => {
    try { await api.put(`/gestor/mensalidades/${id}/confirmar?forma_pagamento=pix`); toast.success('Pagamento confirmado'); fetchData(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const cancelar = async (id) => {
    if (!await confirm('Cancelar Mensalidade', 'Tem certeza que deseja cancelar esta mensalidade?')) return;
    try { await api.put(`/gestor/mensalidades/${id}/cancelar`); toast.success('Cancelada'); fetchData(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const gerarPix = async (id) => {
    try {
      const r = await api.post(`/gestor/mensalidades/${id}/gerar-pix`);
      setPixDialog(r.data);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };


  const visualizarComprovante = async (id) => {
    try {
      const res = await api.get(`/gestor/mensalidades/${id}/comprovante`, {
        responseType: 'blob'
      });

      const type = res.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([res.data], { type });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `comprovante-${id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

    } catch (err) {
      toast.error('Erro ao baixar comprovante');
    }
  };

  const copyPix = () => {
    if (pixDialog?.pix_code) {
      navigator.clipboard.writeText(pixDialog.pix_code);
      toast.success('Codigo PIX copiado!');
    }
  };

  const competenciasDisponiveis = [...new Set(
    mensalidades
      .map(m => m.competencia)
      .filter(Boolean)
  )].sort((a, b) => String(b).localeCompare(String(a)));

  const alunoPorId = new Map(
    alunos.map(aluno => [String(aluno.id), aluno])
  );

  const limparFiltros = () => {
    setSearch('');
    setFilterStatus('todos');
    setFiltroTurma('todas');
    setFiltroCategoria('todas');
    setFiltroPlano('todos');
    setFiltroVencimento('todos');
    setFiltroCompetencia('todas');
    setOrdenacao('vencimento-recente');
  };

  const filtrosAtivos =
    Number(search.trim() !== '') +
    Number(filterStatus !== 'todos') +
    Number(filtroTurma !== 'todas') +
    Number(filtroCategoria !== 'todas') +
    Number(filtroPlano !== 'todos') +
    Number(filtroVencimento !== 'todos') +
    Number(filtroCompetencia !== 'todas');

  const ordemStatus = {
    atrasado: 1,
    comprovante_enviado: 2,
    aguardando_pagamento: 3,
    pago: 4,
    cancelado: 5
  };

  const filtered = mensalidades
    .filter(m => {
      const termo = search.trim().toLowerCase();

      const aluno = alunoPorId.get(String(m.aluno_id)) ||
        alunos.find(a => a.name === m.aluno_nome);

      const matchSearch =
        termo === '' ||
        m.aluno_nome?.toLowerCase().includes(termo) ||
        m.competencia?.toLowerCase().includes(termo) ||
        aluno?.email?.toLowerCase().includes(termo) ||
        aluno?.phone?.toLowerCase().includes(termo) ||
        aluno?.cpf?.toLowerCase().includes(termo);

      const turmaIds = Array.isArray(aluno?.turma_ids)
        ? aluno.turma_ids.map(String)
        : aluno?.turma_id
          ? [String(aluno.turma_id)]
          : [];

      const matchTurma =
        filtroTurma === 'todas' ||
        turmaIds.includes(String(filtroTurma));

      const matchCategoria =
        filtroCategoria === 'todas' ||
        categoriaCompletaDoAluno(aluno) === filtroCategoria;

      const planoMensalidade = m.plano_id || aluno?.plano_id;

      const matchPlano =
        filtroPlano === 'todos' ||
        String(planoMensalidade || '') === String(filtroPlano);

      const diaVencimento = m.data_vencimento
        ? String(m.data_vencimento).split('T')[0].split('-')[2]
        : '';

      const matchVencimento =
        filtroVencimento === 'todos' ||
        String(Number(diaVencimento || 0)) === filtroVencimento;

      const matchCompetencia =
        filtroCompetencia === 'todas' ||
        m.competencia === filtroCompetencia;

      const matchStatus =
        filterStatus === 'todos' ||
        m.status === filterStatus;

      return (
        matchSearch &&
        matchTurma &&
        matchCategoria &&
        matchPlano &&
        matchVencimento &&
        matchCompetencia &&
        matchStatus
      );
    })
    .sort((a, b) => {
      if (ordenacao === 'status-prioridade') {
        return (ordemStatus[a.status] || 99) -
          (ordemStatus[b.status] || 99);
      }

      if (ordenacao === 'status-pagos') {
        const pagoA = a.status === 'pago' ? 0 : 1;
        const pagoB = b.status === 'pago' ? 0 : 1;

        return pagoA - pagoB;
      }

      if (ordenacao === 'status-aguardando') {
        const aguardandoA =
          a.status === 'aguardando_pagamento' ||
          a.status === 'comprovante_enviado'
            ? 0
            : 1;

        const aguardandoB =
          b.status === 'aguardando_pagamento' ||
          b.status === 'comprovante_enviado'
            ? 0
            : 1;

        return aguardandoA - aguardandoB;
      }

      if (ordenacao === 'status-inadimplentes') {
        const atrasadoA = a.status === 'atrasado' ? 0 : 1;
        const atrasadoB = b.status === 'atrasado' ? 0 : 1;

        return atrasadoA - atrasadoB;
      }

      if (ordenacao === 'nome-az') {
        return (a.aluno_nome || '').localeCompare(
          b.aluno_nome || '',
          'pt-BR'
        );
      }

      if (ordenacao === 'nome-za') {
        return (b.aluno_nome || '').localeCompare(
          a.aluno_nome || '',
          'pt-BR'
        );
      }

      const dataA = new Date(
        `${String(a.data_vencimento || '').split('T')[0]}T00:00:00`
      ).getTime();

      const dataB = new Date(
        `${String(b.data_vencimento || '').split('T')[0]}T00:00:00`
      ).getTime();

      if (ordenacao === 'vencimento-antigo') {
        return (Number.isNaN(dataA) ? Infinity : dataA) -
          (Number.isNaN(dataB) ? Infinity : dataB);
      }

      return (Number.isNaN(dataB) ? 0 : dataB) -
        (Number.isNaN(dataA) ? 0 : dataA);
    });

  const now = new Date();
  const defaultComp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="mensalidades-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">MENSALIDADES</h1>
        <div className="flex gap-2">
          <Button onClick={() => { setGerarForm({ competencia: defaultComp }); setGerarDialog(true); }} variant="outline" className="border-[#27272A] text-zinc-300 hover:bg-[#1C1C1E]" data-testid="gerar-mensalidades-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Gerar Automaticas
          </Button>
          <Button onClick={() => { setForm({ aluno_id: '', plano_id: '', valor: '', data_vencimento: '', competencia: defaultComp, observacoes: '' }); setDialogOpen(true); }} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="add-mensalidade-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova Mensalidade
          </Button>
        </div>
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              className="pl-10 bg-[#0A0A0A] border-[#27272A]"
              placeholder="Buscar por aluno, competência, e-mail, telefone ou CPF..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <Select value={filtroTurma} onValueChange={setFiltroTurma}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Turma" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#27272A]">
                <SelectItem value="todas">Todas as turmas</SelectItem>
                {turmas.map(t => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#27272A]">
                <SelectItem value="todas">Todas as categorias</SelectItem>

                {['feminino', 'masculino'].map(sexo =>
                  categoriasFiltro.map(categoria => (
                    <SelectItem
                      key={`${sexo}:${categoria.key}`}
                      value={`${sexo}:${categoria.key}`}
                    >
                      {sexo === 'feminino' ? 'Feminino' : 'Masculino'} — {categoria.label}
                    </SelectItem>
                  ))
                )}

                {categoriasFiltro.map(categoria => (
                  <SelectItem
                    key={`nao_informado:${categoria.key}`}
                    value={`nao_informado:${categoria.key}`}
                  >
                    Sexo não informado — {categoria.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filtroPlano} onValueChange={setFiltroPlano}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Plano" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#27272A]">
                <SelectItem value="todos">Todos os planos</SelectItem>
                {planos.map(p => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filtroVencimento} onValueChange={setFiltroVencimento}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Vencimento" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#27272A]">
                <SelectItem value="todos">Todos os vencimentos</SelectItem>
                {[...Array(31)].map((_, i) => {
                  const dia = String(i + 1);

                  return (
                    <SelectItem key={dia} value={dia}>
                      Vencimento dia {dia}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Select value={filtroCompetencia} onValueChange={setFiltroCompetencia}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Competência" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#27272A]">
                <SelectItem value="todas">Todas as competências</SelectItem>
                {competenciasDisponiveis.map(competencia => (
                  <SelectItem key={competencia} value={competencia}>
                    {competencia}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#27272A]">
                <SelectItem value="todos">Todos os status</SelectItem>
                <SelectItem value="pago">Pago</SelectItem>
                <SelectItem value="aguardando_pagamento">Aguardando</SelectItem>
                <SelectItem value="comprovante_enviado">Comprovante enviado</SelectItem>
                <SelectItem value="atrasado">Inadimplente</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>

            <Select value={ordenacao} onValueChange={setOrdenacao}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#27272A]">
                <SelectItem value="status-prioridade">
                  Prioridade financeira
                </SelectItem>
                <SelectItem value="status-inadimplentes">
                  Inadimplentes primeiro
                </SelectItem>
                <SelectItem value="status-aguardando">
                  Aguardando primeiro
                </SelectItem>
                <SelectItem value="status-pagos">
                  Pagos primeiro
                </SelectItem>
                <SelectItem value="vencimento-recente">
                  Vencimento mais recente
                </SelectItem>
                <SelectItem value="vencimento-antigo">
                  Vencimento mais antigo
                </SelectItem>
                <SelectItem value="nome-az">Aluno: A–Z</SelectItem>
                <SelectItem value="nome-za">Aluno: Z–A</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-zinc-400">
              Mostrando{' '}
              <span className="font-semibold text-white">
                {filtered.length}
              </span>
              {' '}de{' '}
              <span className="font-semibold text-white">
                {mensalidades.length}
              </span>
              {' '}mensalidades

              {filtrosAtivos > 0 && (
                <span className="ml-2 text-[var(--cor-primaria,#007AFF)]">
                  · {filtrosAtivos}{' '}
                  {filtrosAtivos === 1 ? 'filtro ativo' : 'filtros ativos'}
                </span>
              )}
            </p>

            {filtrosAtivos > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={limparFiltros}
                className="border-[#3F3F46] text-zinc-300 hover:bg-[#27272A] hover:text-white"
              >
                Limpar filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A] hover:bg-transparent">
                <TableHead className="text-zinc-400">Aluno</TableHead>
                <TableHead className="text-zinc-400">Competencia</TableHead>
                <TableHead className="text-zinc-400">Valor</TableHead>
                <TableHead className="text-zinc-400">Vencimento</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400 text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">Carregando...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">Nenhuma mensalidade</TableCell></TableRow>
              ) : filtered.map(m => (
                <TableRow key={m.id} className="border-[#27272A] hover:bg-[#1C1C1E]" data-testid={`mensalidade-row-${m.id}`}>
                  <TableCell className="text-white font-medium">{m.aluno_nome}</TableCell>
                  <TableCell className="text-zinc-400">{m.competencia}</TableCell>
                  <TableCell className="text-zinc-300">R$ {m.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-zinc-400">{m.data_vencimento?.split('T')[0] || m.data_vencimento}</TableCell>
                  <TableCell><Badge className={statusColors[m.status]}>{statusLabel[m.status] || m.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    {m.status !== 'pago' && m.status !== 'cancelado' && (
                      <>
                        {m.comprovante_url && (
                          <Button size="sm" variant="ghost" onClick={() => visualizarComprovante(m.id)} className="text-blue-400 hover:text-blue-300" title="Ver comprovante">
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}

                        {(() => {
                          const aluno =
                            alunoPorId.get(String(m.aluno_id)) ||
                            alunos.find(a => a.name === m.aluno_nome);

                          const acaoWhatsApp =
                            getAcaoWhatsAppMensalidade(m, aluno);

                          if (!acaoWhatsApp) return null;

                          return (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                window.open(
                                  acaoWhatsApp.link,
                                  '_blank',
                                  'noopener,noreferrer'
                                )
                              }
                              className={
                                acaoWhatsApp.tipo === 'cobranca'
                                  ? 'text-red-400 hover:text-red-300'
                                  : acaoWhatsApp.tipo === 'vence_hoje'
                                    ? 'text-amber-400 hover:text-amber-300'
                                    : 'text-green-400 hover:text-green-300'
                              }
                              data-testid={`whatsapp-mensalidade-${m.id}`}
                              title={acaoWhatsApp.titulo}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          );
                        })()}

                        <Button size="sm" variant="ghost" onClick={() => confirmar(m.id)} className="text-green-400 hover:text-green-300" data-testid={`confirm-${m.id}`} title="Confirmar"><Check className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => gerarPix(m.id)} className="text-[#007AFF] hover:text-blue-300" data-testid={`pix-${m.id}`} title="Gerar PIX"><QrCode className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => cancelar(m.id)} className="text-zinc-400 hover:text-red-400" data-testid={`cancel-${m.id}`} title="Cancelar"><X className="w-4 h-4" /></Button>
                      </>
                    )}
                    {m.pix_code && <Button size="sm" variant="ghost" onClick={() => setPixDialog({ pix_code: m.pix_code, valor: m.valor })} className="text-[#007AFF]" title="Ver PIX"><QrCode className="w-4 h-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">Nova Mensalidade</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Aluno</Label>
              <Select value={form.aluno_id} onValueChange={v => { setForm(f => ({ ...f, aluno_id: v })); const a = alunos.find(a => a.id === v); if (a?.plano_id) { const p = planos.find(p => p.id === a.plano_id); if (p) setForm(f => ({ ...f, plano_id: p.id, valor: String(p.valor) })); } }}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A]">{alunos.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Valor (R$)</Label><Input type="number" step="0.01" value={form.valor} onChange={e => setForm({...form, valor: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Competencia (YYYY-MM)</Label><Input value={form.competencia} onChange={e => setForm({...form, competencia: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" placeholder="2026-04" required /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Vencimento</Label><Input type="date" value={form.data_vencimento} onChange={e => setForm({...form, data_vencimento: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Observacoes</Label><Textarea value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD]">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Gerar Dialog */}
      <Dialog open={gerarDialog} onOpenChange={setGerarDialog}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">Gerar Mensalidades</DialogTitle></DialogHeader>
          <form onSubmit={handleGerar} className="space-y-4">
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Competencia (YYYY-MM)</Label><Input value={gerarForm.competencia} onChange={e => setGerarForm({...gerarForm, competencia: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required /></div>
            <p className="text-xs text-zinc-500">O dia de vencimento sera definido automaticamente pelo plano de cada aluno.</p>
            <p className="text-xs text-zinc-500">Gera mensalidades para todos os alunos ativos com plano vinculado.</p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setGerarDialog(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD]">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gerar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PIX Dialog */}
      <Dialog open={!!pixDialog} onOpenChange={() => setPixDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2"><QrCode className="w-5 h-5 text-[#007AFF]" /> PIX</DialogTitle></DialogHeader>
          {pixDialog && (
            <div className="space-y-4 text-center">
              <p className="text-2xl font-black font-['Barlow_Condensed'] text-white">R$ {pixDialog.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              <div className="bg-white p-4 rounded-lg inline-block mx-auto"><QRCodeSVG value={pixDialog.pix_code} size={200} /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Copia e Cola</p>
                <div className="flex gap-2">
                  <Input value={pixDialog.pix_code} readOnly className="bg-[#0A0A0A] border-[#27272A] text-xs" data-testid="pix-code-input" />
                  <Button size="icon" variant="outline" onClick={copyPix} className="border-[#27272A] shrink-0" data-testid="copy-pix-btn"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <p className="text-xs text-zinc-500">Aguardando confirmacao manual do pagamento.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  );
}
